import { useCallback, useEffect, useState } from "react";
import {
  docBelongsToActiveWorkspace,
  getActiveDesignFirmId,
  useWorkspaceStore,
} from "ui";
import { FFEScheduleInterface } from "ts-interfaces";
import { getFFESchedulesForUser } from "../v2/apis/ffeSchedulesApi";
import { activeTeamScope } from "../v2/apis/accessQueries";
import { useTeamScope, useTeamScopeReady } from "./useTeamScope";

// Module-level SWR cache (same spirit as useProjectDataFetcher's Map cache):
// revisits serve the cached list instantly and revalidate in the background,
// and concurrent mounts share a single fetch. Keyed on uid + active firm (+ the
// sharingV2 query) so a workspace, account or flag switch bypasses it.
// Schedule writes elsewhere (editor, create, archive) don't invalidate it —
// the always-on revalidate self-heals on the next mount.
let cache: { key: string; schedules: FFEScheduleInterface[] } | null = null;
let inflight: {
  key: string;
  promise: Promise<FFEScheduleInterface[]>;
} | null = null;

const cacheKey = (uid: string) =>
  `${uid}|${getActiveDesignFirmId(uid) ?? ""}${activeTeamScope(uid) ? "|v2" : ""}`;

/**
 * Cached cross-project schedules list for this user's active workspace, if
 * any. Lets project-scoped readers (useProjectSchedules) paint instantly from
 * the dashboard's query instead of waiting on their own. Key-checked so a
 * workspace/account switch never serves the previous workspace's list.
 */
export const getCachedUserSchedules = (
  uid: string,
): FFEScheduleInterface[] | null =>
  cache?.key === cacheKey(uid) ? cache.schedules : null;

const fetchUserSchedules = (uid: string, key: string) => {
  if (inflight?.key === key) return inflight.promise;
  const promise = getFFESchedulesForUser(
    uid,
    getActiveDesignFirmId(uid),
    activeTeamScope(uid),
  )
    .then((all) => {
      const schedules = all.filter((s) => docBelongsToActiveWorkspace(s, uid));
      cache = { key, schedules };
      return schedules;
    })
    .finally(() => {
      if (inflight?.key === key) inflight = null;
    });
  inflight = { key, promise };
  return promise;
};

/**
 * The caller's non-archived FF&E schedules across all projects, newest
 * first, filtered to the active workspace (Option B, same convention as
 * useProjectSchedules). Read-only — powers the dashboard's recents and the
 * getting-started checklist.
 */
export const useUserSchedules = (uid: string | undefined) => {
  const [schedules, setSchedules] = useState<FFEScheduleInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Manual re-fetch for error/Retry UIs — a failed fetch leaves no cache
  // entry, so re-running the effect goes back to the network.
  const [retryNonce, setRetryNonce] = useState(0);
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);
  // Re-fetch when the active workspace flips (personal ↔ team), or when the
  // team's sharingV2 flag lands.
  const activeIdByUid = useWorkspaceStore((s) => s.activeIdByUid);
  const teamScope = useTeamScope(uid);
  const scopeReady = useTeamScopeReady(uid);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setSchedules([]);
      setLoading(false);
      setLoadError(false);
      return;
    }
    if (!scopeReady) return; // the team's flag is still loading
    const key = cacheKey(uid);
    const hasCache = cache?.key === key;
    if (hasCache) {
      setSchedules(cache!.schedules);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setLoadError(false);
    fetchUserSchedules(uid, key)
      .then((fresh) => {
        if (!cancelled) setSchedules(fresh);
      })
      .catch((e) => {
        console.error("Error loading user schedules:", e);
        // Keep cached rows if we have them; only surface the error when the
        // list would otherwise be empty (nothing to show the user). A cached
        // *empty* result still counts as empty, so check for rows — not just a
        // cache entry — or a failed refresh over an empty cache reads as "no
        // schedules" instead of an error.
        const hasCachedRows = hasCache && cache!.schedules.length > 0;
        if (!cancelled && !hasCachedRows) {
          setSchedules([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, activeIdByUid, teamScope, scopeReady, retryNonce]);

  return { schedules, loading, loadError, retry };
};
